import type {
  BadgesCatalogModel,
  CatalogBadgesRubricsUnion
} from '@schema/cart';
import type { IconType, ProductMultiPrice } from '../types';

export const getBadgeColor = (
  type:
    | 'hit'
    | 'newbee'
    | 'promotion'
    | 'superprice'
    | 'discount'
    | 'digital'
    | 'gift'
    | string
): string => {
  switch (type) {
    case 'hit':
      return '#FF985E';
    case 'newbee':
      return '#B32442';
    case 'promotion':
      return '#FFDC5C';
    case 'superprice':
      return '#A73456';
    case 'discount':
      return '#FFDC5C';
    case 'digital':
      return '#AB40FF';
    case 'gift':
      return '#FF406E';
    default:
      return '#000';
  }
};

const getBadgeTitle = (type: string): string => {
  switch (type) {
    case 'hit':
      return 'Хит';
    case 'newbee':
      return 'Новинка';
    case 'promotion':
      return 'Акция';
    case 'superprice':
      return 'суперцена';
    default:
      return '';
  }
};

const hitBadge = {
  color: '#FF985E',
  id: 'hit',
  link: '/catalog/hit',
  title: 'Хит',
  tooltip: null
};

export interface Badge {
  id?: string | number;
  link: string | null;
  color?: string;
  title: string;
  tooltip?: string | null;
  icon?: IconType;
  order?: number;
}

interface ProductAsGift {
  id: string;
}

export const prepareOldBadges = (
  badges: CatalogBadgesRubricsUnion,
  discount?: number | null,
  isDigital?: boolean,
  multiPricePromo?: ProductMultiPrice | null,
  productAsGift?: ProductAsGift | null
): Badge[] => {
  if (typeof badges === 'object' && badges !== null) {
    const badgesKeys = Object.keys(badges);

    // нормализируем данные бейджей
    if (Array.isArray(badgesKeys) && badgesKeys?.length > 0) {
      const preparedOldBadges = badgesKeys
        .map(item => {
          const i = badges[item];
          // добавляем бейдж со скидкой
          if (item === 'discount' && discount && !multiPricePromo) {
            return {
              id: item,
              link: i.link,
              color: getBadgeColor(item),
              title: `-${discount}%`,
              tooltip: null
            };
          }
          // фильтруем по identify
          if (!i.available || item === 'natural') {
            return null;
          }

          return {
            id: item,
            link: i.link,
            color: getBadgeColor(item),
            title: getBadgeTitle(item),
            tooltip: item === 'promotion' ? i.title : null
          };
        })
        .filter(Boolean) as Badge[];
      // добавляем бейдж мультицена
      if (
        multiPricePromo &&
        typeof multiPricePromo?.buy_at_once === 'number' &&
        multiPricePromo?.buy_at_once > 0
      ) {
        preparedOldBadges.push({
          id: 'multi-price',
          link: null,
          title: `Первые ${multiPricePromo.buy_at_once} - ${
            multiPricePromo?.weightPriceData
              ? multiPricePromo?.weightPriceData?.price
              : multiPricePromo.price_active
          }₽`,
          color: '#FF406E'
        });
      }

      if (isDigital) {
        preparedOldBadges.push({
          id: 999,
          link: '', // Добавить ссылку на цифровой каталог
          color: getBadgeColor('digital'),
          title: 'Цифровой товар'
        });
      }
      // Товар в подарок при покупке
      if (productAsGift) {
        preparedOldBadges.push({
          id: 996,
          link: `/product/${productAsGift.id}`,
          color: getBadgeColor('gift'),
          title: '+ Подарок'
        });
      }

      return preparedOldBadges;
    }
  }
  return [];
};

export const prepareNewBadges = (badgesList: BadgesCatalogModel[]): Badge[] => {
  // нормализируем данные новых бейджей
  if (badgesList?.length) {
    const arr = [...badgesList].filter(
      item => item.id && [1, 4, 7, 16].includes(item.id)
    );
    return arr?.length
      ? (arr.map(item =>
          item.id === 16
            ? {
                ...item,
                link: `/catalog/${item.identify ?? ''}`,
                color: '#FF985E'
              }
            : {
                ...item,
                id: item.id || 69,
                link: `/catalog/${item.identify ?? ''}`
              }
        ) as Badge[])
      : [];
  }
  return [];
};

export const prepareAllBadges = (arr: Badge[]): Badge[] => {
  if (arr?.length === 1) {
    return arr;
  }

  let result = [...arr];

  if (result?.length) {
    const hasHitBadge =
      result.findIndex(item => item.id === 'hit' || item.id === 16) !== -1;

    // удаляем хиты
    if (hasHitBadge) {
      result = result.filter(item => item.id !== 'hit' && item.id !== 16);
    }

    // если есть и хит и промо оставляем хит
    if (result.length > 1) {
      const promotionIndex = result.findIndex(item => item.id === 'promotion');
      if (hasHitBadge && promotionIndex >= 0) {
        result.splice(promotionIndex, 1);
      }
    }

    // добавляем хит
    if (hasHitBadge && result.findIndex(i => i.id === 'promotion') === -1) {
      result.splice(0, 0, hitBadge);
    }
    const orderedResult = result.slice(0, 3).map(item => {
      const order =
        item.id === 'hit' || item.id === 'promotion'
          ? 0
          : item.id === 'discount'
          ? 1
          : 2;
      return {
        ...item,
        order
      };
    });
    return orderedResult;
  }

  return result;
};

export const prepareBottomBadges = (
  hasIsOur: number | null | undefined,
  badgesList: BadgesCatalogModel[]
): Badge[] => {
  const isOur = hasIsOur && {
    id: 13,
    color: '#B32442',
    title: 'Наша марка',
    onClick: undefined
  };
  const list =
    badgesList?.length > 0
      ? ([...badgesList].filter(
          i => i.id && ![1, 4, 7, 13, 16].includes(i.id)
        ) as Badge[])
      : [];
  return [isOur, ...list].filter(i => i) as Badge[];
};
